import { useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/translations";
import { routes, site } from "@/data/site";
import { Logo } from "@/components/Logo";
import { Send } from "lucide-react";

// Newsletter: stesso endpoint già attivato del form contatti (destinatario info@cspdlm.it)
const NEWSLETTER_URL = "https://formsubmit.co/ajax/info@cspdlm.it";

export const Footer = () => {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [honey, setHoney] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "ok" | "err" | "invalid">("idle");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (state === "sending") return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())) {
      setState("invalid");
      return;
    }
    if (honey) return;
    setState("sending");
    try {
      const res = await fetch(NEWSLETTER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          iscrizione: "newsletter dal sito",
          _subject: "Iscrizione alla newsletter — sito cspdlm.it",
          _template: "table",
          _captcha: "false",
          _honey: honey,
        }),
      });
      const data = await res.json().catch(() => null);
      const ok = data?.success === true || data?.success === "true";
      if (!res.ok || !ok) throw new Error(String(res.status));
      setState("ok");
      setEmail("");
    } catch {
      setState("err");
    }
  };

  return (
    <footer className="bg-ink text-paper">
      <div className="container-x grid grid-cols-1 gap-12 py-16 md:grid-cols-12">
        <div className="md:col-span-4">
          <Logo invert />
          <p className="mt-5 max-w-sm text-sm leading-relaxed text-paper/70">{t("brand.tag")}</p>
          <p className="mt-4 max-w-sm text-xs leading-relaxed text-paper/50">{t("footer.note")}</p>
        </div>
        <nav className="md:col-span-2" aria-label="Menu footer" data-testid="footer-menu">
          <span className="eyebrow mb-4 block text-paper/50">{t("footer.menu")}</span>
          <ul className="space-y-2.5">
            {routes.map((r) => (
              <li key={r.path}>
                <Link to={r.path} data-testid={`footer-${r.testid}`} className="link-underline text-sm text-paper/80 hover:text-paper">
                  {t(r.key)}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <div className="md:col-span-3">
          <span className="eyebrow mb-4 block text-paper/50">{t("footer.newsletter_title")}</span>
          <p className="text-sm leading-relaxed text-paper/70">{t("footer.newsletter_intro")}</p>
          <form onSubmit={submit} noValidate className="mt-5" data-testid="footer-newsletter-form">
            <div className="honeypot-field" aria-hidden="true">
              <label htmlFor="nl-website">Website</label>
              <input
                id="nl-website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={honey}
                onChange={(e) => setHoney(e.target.value)}
                data-testid="footer-newsletter-honeypot"
              />
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <input
                type="email"
                value={email}
                placeholder={t("footer.newsletter_ph")}
                aria-label={t("footer.newsletter_ph")}
                data-testid="footer-newsletter-input"
                className="w-full flex-1 border border-white/20 bg-white/5 px-3.5 py-2.5 text-sm text-paper outline-none transition-colors placeholder:text-paper/40 focus:border-paper/60"
                onChange={(e) => setEmail(e.target.value)}
              />
              <button
                type="submit"
                disabled={state === "sending"}
                data-testid="footer-newsletter-submit-btn"
                className="inline-flex items-center justify-center gap-2 bg-paper px-5 py-2.5 text-[12px] font-semibold uppercase tracking-[0.12em] text-ink transition-colors duration-300 hover:bg-bordeaux hover:text-paper disabled:cursor-wait disabled:opacity-60"
              >
                {state === "sending" ? t("footer.newsletter_sending") : t("footer.newsletter_btn")}
                <Send className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </div>
            {state === "ok" && (
              <p data-testid="footer-newsletter-success" className="mt-3 text-xs leading-relaxed text-emerald-300">
                {t("footer.newsletter_ok")}
              </p>
            )}
            {(state === "invalid" || state === "err") && (
              <p data-testid="footer-newsletter-error" className="mt-3 text-xs leading-relaxed text-red-300">
                {state === "invalid" ? t("footer.newsletter_err") : t("footer.newsletter_err_send")}
              </p>
            )}
          </form>
        </div>
        <div className="md:col-span-3">
          <span className="eyebrow mb-4 block text-paper/50">{t("footer.legale")}</span>
          <ul className="space-y-2.5 text-sm">
            <li>
              <Link to="/privacy" data-testid="footer-privacy-link" className="link-underline text-paper/80 hover:text-paper">
                {t("footer.privacy")}
              </Link>
            </li>
            <li>
              <Link to="/cookie-policy" data-testid="footer-cookie-link" className="link-underline text-paper/80 hover:text-paper">
                {t("footer.cookie")}
              </Link>
            </li>
            <li className="pt-2 text-paper/60">
              <a href={`mailto:${site.email}`} data-testid="footer-email-link" className="link-underline text-paper/80 hover:text-paper">
                {site.email}
              </a>
            </li>
            <li className="text-paper/60">{site.domain}</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="container-x flex flex-col items-start justify-between gap-2 py-6 text-xs text-paper/50 sm:flex-row sm:items-center">
          <span data-testid="footer-rights">{t("footer.rights")}</span>
          <span className="font-serif italic tracking-widest">MMXXVI</span>
        </div>
      </div>
    </footer>
  );
};
